import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function EastRidingOfYorkshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="East Riding Pothole Claims | RED Spend Rating | Fixtyer"
        description="East Riding of Yorkshire Council earns a RED DfT spend scorecard while 22% of unclassified roads are in RED condition and it filled 163,865 potholes in five years. See what the data means for your claim."
        canonical="/councils/east-riding-of-yorkshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'East Riding of Yorkshire', url: '/councils/east-riding-of-yorkshire' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'East Riding has a RED spend rating — what does that mean for my pothole claim?',
            answer: 'The DfT Spend scorecard is RED — one of only a handful of councils nationally — because East Riding\'s projected capital spend falls short of its DfT allocation and its resurfacing programme is minimal relative to network size. For 2025/26 the council forecasts £20.413m capital spend against £22.015m allocated, and plans just 12km of carriageway resurfacing across 3,551km. That under-investment context matters when the council argues it lacked resources to prevent your defect — but Section 58 still turns on whether your specific pothole was reasonably inspected and repaired.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 1,911km — 54% of the East Riding network — and 22% were in RED condition at the last survey in 2024. That is roughly 420km of estate roads, village lanes and rural routes. The council surveys U-roads by Coarse Visual Inspection via RoadAI, not SCANNER, and admits SCANNER vehicles have historically been too large for many estate routes. Your strongest structural arguments often sit on this road class.',
          },
          {
            question: 'East Riding says its A-roads are among the best in the country — does that weaken my claim?',
            answer: 'Only if your incident was on an A-road. The council reports just 1% of A-roads in poor condition needing maintenance — and A-roads carry nearly half of traffic on only 10% of the network. If your pothole was on a B, C or unclassified road, the council\'s own data shows a different picture: approximately 4% of B/C roads in RED condition and U-roads deteriorating 2–4% annually under current investment levels.',
          },
          {
            question: 'Pothole repairs fell to 31,015 in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. East Riding still filled 31,015 potholes in 2024/25 — about 85 every day — and reactive pothole work consumed 22% of reactive maintenance spend that year, up from 16% in 2020/21. The five-year total is 163,865 pothole repairs. The council\'s own goal is to reduce reactive costs by shifting to preventative work — but U-roads are still deteriorating annually.',
          },
          {
            question: 'Why does East Riding not use SCANNER surveys on unclassified roads?',
            answer: 'The council states SCANNER is not used on the unclassified network because it is more expensive, provides analysis beyond what is necessary for U-roads, and the survey vehicle has historically been too large to traverse many estate routes. CVI via RoadAI is cheaper but less precise. A council choosing simpler surveys on 54% of its network cannot later claim the same detailed condition knowledge it has on SCANNER-surveyed A and B roads.',
          },
        ]}
      />
      <ServiceSchema
        councilName="East Riding of Yorkshire Council"
        councilSlug="east-riding-of-yorkshire"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/east-riding-of-yorkshire"
        headline="East Riding of Yorkshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">East Riding of Yorkshire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            East Riding: RED Spend Rating, Deteriorating Local Roads
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            East Riding of Yorkshire Council is one of the few authorities nationally with a{' '}
            <strong className="text-rose-400">RED DfT Spend scorecard</strong> — while its overall rating stays{' '}
            <strong className="text-amber-400">AMBER</strong>. The council forecasts capital spend below its 2025/26
            DfT allocation, plans just 12km of resurfacing across 3,551km, and admits unclassified roads are deteriorating
            2–4% a year even as it filled <strong className="text-rose-400">163,865 potholes in five years</strong>.
          </p>

          {/* Key stat callout */}
          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">RED</div>
            <div className="text-white font-medium mt-2">DfT Spend scorecard — under-investment relative to allocation</div>
            <div className="text-brand-300 text-sm mt-1">
              2025/26 capital spend projected at £20.413m against £22.015m DfT allocation, with only 12km of
              carriageway resurfacing planned — on a 3,551km network where 1,911km of U-roads are CVI-surveyed and
              22% sit in RED condition.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Spend Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Why The Spend Scorecard Is RED
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of capital allocation versus spend from East Riding's own transparency report — and what the
            DfT's RED rating signals
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">DfT capital allocated</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Capital spend</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-emerald-700">Preventative</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">£22,622k</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">£18,897k</td>
                  <td className="px-6 py-4 text-center">£10,561k</td>
                  <td className="px-6 py-4 text-center">72%</td>
                  <td className="px-6 py-4 text-center">18%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">£14,893k</td>
                  <td className="px-6 py-4 text-center">£19,793k</td>
                  <td className="px-6 py-4 text-center">£11,344k</td>
                  <td className="px-6 py-4 text-center">74%</td>
                  <td className="px-6 py-4 text-center">15%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">£13,793k</td>
                  <td className="px-6 py-4 text-center">£19,288k</td>
                  <td className="px-6 py-4 text-center">£11,723k</td>
                  <td className="px-6 py-4 text-center">72%</td>
                  <td className="px-6 py-4 text-center">17%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">£17,998k</td>
                  <td className="px-6 py-4 text-center">£20,059k</td>
                  <td className="px-6 py-4 text-center">£10,474k</td>
                  <td className="px-6 py-4 text-center">74%</td>
                  <td className="px-6 py-4 text-center">16%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">£15,546k</td>
                  <td className="px-6 py-4 text-center">£19,657k</td>
                  <td className="px-6 py-4 text-center">£10,710k</td>
                  <td className="px-6 py-4 text-center">76%</td>
                  <td className="px-6 py-4 text-center">13%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2025/26 (proj.)</td>
                  <td className="px-6 py-4 text-center font-bold">£22,015k</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">£20,413k</td>
                  <td className="px-6 py-4 text-center">£10,849k</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">77%</td>
                  <td className="px-6 py-4 text-center">12%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-rose-600 mb-2">12km</div>
              <div className="text-surface-700 text-sm">Carriageway resurfacing planned 2025/26 (DfT projection)</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-amber-600 mb-2">222km</div>
              <div className="text-surface-700 text-sm">Preventative carriageway treatments planned 2025/26</div>
            </div>
            <div className="bg-brand-950 text-white rounded-xl p-6 text-center">
              <div className="text-3xl font-bold text-rose-400 mb-2">£1.6m</div>
              <div className="text-brand-300 text-sm">Gap between 2025/26 DfT allocation and projected capital spend</div>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">The Resurfacing Gap</h3>
            <p className="text-brand-200 mb-4">
              East Riding manages <strong className="text-white">3,551km of carriageway</strong> with a modern-day
              replacement value of £3.2 billion. Yet DfT-published treatment projections show only{' '}
              <strong className="text-rose-400">12km of resurfacing</strong> planned for 2025/26 — less than one-third
              of one per cent of the network receiving structural renewal in a single year.
            </p>
            <p className="text-white font-medium">
              That is the spend story behind the RED scorecard: preventative surface dressing on hundreds of kilometres,
              but minimal full resurfacing — while pothole reactive spend rose from 16% to 22% of reactive maintenance
              between 2020/21 and 2024/25.
            </p>
          </div>
        </div>
      </section>

      {/* Network & Condition */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Excellent A-roads, middling B/C roads, and a deteriorating unclassified network — on 3,551km across rural
            Yorkshire
          </p>

          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-white border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">373km</div>
              <div className="text-sm text-surface-600">A-roads (10.5% of network)</div>
            </div>
            <div className="bg-white border border-surface-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-brand-900">1,267km</div>
              <div className="text-sm text-surface-600">B and C roads (35.7%)</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-amber-700">1,911km</div>
              <div className="text-sm text-amber-800">Unclassified roads (53.8%)</div>
            </div>
          </div>

          {/* A-roads */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-emerald-900 mb-4">A-roads (373km): among the best nationally</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-emerald-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-emerald-100 border-b border-emerald-200">
                    <th className="px-4 py-2 text-left font-semibold text-emerald-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { year: '2020', red: '1%', amber: '17%', green: '82%' },
                    { year: '2021', red: '0%', amber: '13%', green: '87%' },
                    { year: '2022', red: '1%', amber: '24%', green: '75%' },
                    { year: '2023', red: '2%', amber: '27%', green: '71%' },
                    { year: '2024', red: '1%', amber: '21%', green: '78%' },
                  ].map((row) => (
                    <tr key={row.year} className="border-b border-emerald-100">
                      <td className="px-4 py-2 font-mono">{row.year}</td>
                      <td className="px-4 py-2 text-center">{row.red}</td>
                      <td className="px-4 py-2 text-center">{row.amber}</td>
                      <td className="px-4 py-2 text-center">{row.green}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-emerald-800 text-sm">
              Credit where due: SCANNER surveys 100% of A-roads annually, and only 1% are in poor condition in 2024 —
              though these roads carry nearly half of all traffic.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (1,267km): ~4% RED, stable but not improving</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { year: '2020', red: '4%', amber: '31%', green: '65%' },
                    { year: '2021', red: '2%', amber: '24%', green: '74%' },
                    { year: '2022', red: '2%', amber: '25%', green: '73%' },
                    { year: '2023', red: '4%', amber: '27%', green: '69%' },
                    { year: '2024', red: '4%', amber: '26%', green: '70%' },
                  ].map((row) => (
                    <tr key={row.year} className="border-b border-surface-100">
                      <td className="px-4 py-2 font-mono">{row.year}</td>
                      <td className="px-4 py-2 text-center">{row.red}</td>
                      <td className="px-4 py-2 text-center">{row.amber}</td>
                      <td className="px-4 py-2 text-center">{row.green}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              100% of B roads and 50% of C roads surveyed annually by SCANNER. RED-condition B/C roads sit at roughly
              4% — about 51km needing maintenance intervention at the last survey.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (1,911km): deteriorating 2–4% a year</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-6 py-3 text-left text-sm font-semibold text-amber-900">Year</th>
                    <th className="px-6 py-3 text-center text-sm font-semibold text-amber-900">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {[
                    { year: '2020', red: '24%' },
                    { year: '2021', red: '27%' },
                    { year: '2022', red: '16%' },
                    { year: '2023', red: '18%' },
                    { year: '2024', red: '22%' },
                  ].map((row) => (
                    <tr key={row.year} className="border-b border-amber-100">
                      <td className="px-6 py-4 font-mono text-brand-900">{row.year}</td>
                      <td className="px-6 py-4 text-center font-bold text-rose-600">{row.red}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              At 22% RED in 2024, roughly <strong>420km</strong> of residential streets, estate roads and rural lanes
              should have been considered for maintenance — surveyed by CVI via RoadAI, not SCANNER.
            </p>
          </div>
        </div>
      </section>

      {/* Survey methodology */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Two Networks, Two Survey Standards
          </h2>
          <p className="text-surface-600 mb-8">
            SCANNER laser surveys on busy roads — drive-by CVI on the 1,911km where estates are too narrow for the van
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The SCANNER technology is not used to survey unclassified network as it is a lot more expensive than other methods and provides more detailed analysis that is not necessary for the unclassified network. The vehicle which carries out the survey has historically been too large to traverse many estate routes."
              source="East Riding of Yorkshire Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="However, with current investment levels, the Unclassified road network has been deteriorating by 2-4% annually. This does not mean that the network is unsafe, but it does mean it may not look good."
              source="East Riding of Yorkshire Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">SCANNER routes (A, B, partial C)</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• 100% of A-roads surveyed annually</li>
                <li>• 100% of B roads and 50% of C roads in one direction</li>
                <li>• Laser-based Surface Condition Assessment for the National Network of Roads</li>
                <li>• Best data the council holds — strongest Section 58 defence territory</li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">CVI routes (unclassified)</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• RoadAI from Vaisala — Coarse Visual Inspection format</li>
                <li>• 54% of the network on a simpler, cheaper survey method</li>
                <li>• PAS 2161 five-category methodology mandated from 2026/27</li>
                <li>• Estate routes historically inaccessible to SCANNER vehicles</li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mt-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under Section 41 of the Highways Act 1980, East Riding must maintain highways reasonably. Under Section 58,
              it can defend claims only if it proves a reasonable inspection and repair system. For over half the network,
              ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road CVI-surveyed — and how recently?</li>
              <li>• If 22% of U-roads are RED, what was done about deterioration on yours?</li>
              <li>• Can a council claim detailed knowledge on roads it deliberately does not SCANNER-survey?</li>
              <li>• Does a 2–4% annual deterioration rate on U-roads under current investment meet a reasonable standard?</li>
            </ul>
            <p className="text-white font-medium">
              A council that admits managed decline on local roads cannot treat every pothole claim as unforeseeable.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole conveyor */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            163,865 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive maintenance rising as a share of spend — even as preventative share climbs
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">% of reactive spend on potholes</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">26,969</td>
                  <td className="px-6 py-4 text-center">16%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">29,756</td>
                  <td className="px-6 py-4 text-center">16%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">31,802</td>
                  <td className="px-6 py-4 text-center">14%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">35,323</td>
                  <td className="px-6 py-4 text-center">18%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">31,015</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">22%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">163,865</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~90 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, East Riding fills around 90 potholes per day across its network. Between 2020/21
                and 2024/25, the share of reactive maintenance spend consumed by pothole filling rose from 16% to 22% —
                patching is costing more even as the council shifts toward preventative work.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Planned work 2020–2024</h3>
              <p className="text-amber-800 text-sm">
                The council reports 1,131km of carriageway planned repairs and 225km of footway repairs delivered between
                2020 and 2024 — alongside 12 bridge repairs and 1,350 street lanterns replaced. Preventative delivery is
                real, but U-road deterioration continues under current investment levels.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Strategic admission */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Strategic Routes First — Local Roads Last
          </h2>
          <p className="text-surface-600 mb-8">
            The council's own words on where investment goes — and what that means for rural lanes
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="We have some of the best condition A roads in the country. Only 1% of our A roads are considered to be in a poor condition needing maintenance. This has been our strategy as these roads take almost half the traffic in the East Riding, even though they make up on 10% of our network."
              source="East Riding of Yorkshire Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="While reactive maintenance is necessary, it is more expensive than planned maintenance. Our goal is to reduce these costs by shifting toward preventative strategies that are both timely and cost-effective."
              source="East Riding of Yorkshire Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold text-brand-950 mb-4">The Rural Network Reality</h3>
            <p className="text-surface-600 mb-4">
              East Riding is a predominantly rural authority covering 930 square miles with a scattered population of
              approximately 334,200. People travel further and rely more on cars — yet{' '}
              <strong>53.8% of carriageway length is unclassified</strong>, surveyed by CVI rather than SCANNER, and
              deteriorating under a strategy that prioritises A, B and C routes.
            </p>
            <p className="text-surface-600">
              That is not negligence in the abstract — it is a documented funding choice. For your claim, the question is
              whether that choice was reasonable for <strong>your specific road and defect</strong> at the time you were
              damaged.
            </p>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an AMBER Council With a RED Spend Rating
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: East Riding is not North Lincolnshire — but the spend scorecard is worse
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road condition among the best nationally — 1% poor in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Preventative maintenance share rising — 72% to 77% since 2020/21
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Capital spend exceeded DfT allocation in four of the last five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 1,131km of planned carriageway repairs delivered 2020–2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> AMBER best practice — AI condition monitoring, Live Labs 2 trials, ISO-aligned TAMP
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a competent Section 58 defence on SCANNER-surveyed A and B roads. Generic claims on strategic routes will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED DfT Spend scorecard — projected underspend vs 2025/26 allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Only 12km resurfacing planned for 3,551km — minimal structural renewal
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 22% of U-roads RED — ~420km needing maintenance intervention
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-roads deteriorating 2–4% annually under current investment
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 163,865 pothole repairs in five years — defects forming faster than renewal
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> No SCANNER on 54% of network — CVI on estate routes SCANNER vans cannot reach
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with excellent A-road data and a RED spend rating, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect and road class</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the CVI survey gap and 22% RED network rate are your strongest structural arguments</li>
              <li>• Timing relative to CVI survey cycles and the 2026/27 PAS 2161 methodology change</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and cites East
              Riding's own transparency data — including the RED spend rating and U-road deterioration admissions — where
              they help you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in the East Riding?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A RED spend rating and 163,865 repairs demand a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No RED spend underspend argued</li>
                <li>• No U-road CVI survey gap cited</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ RED DfT spend scorecard documented</li>
                <li>✅ 22% U-road RED condition cited</li>
                <li>✅ 163,865 pothole repairs in five years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to East Riding</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=east-riding-of-yorkshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My East Riding Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                East Riding has a RED spend rating — what does that mean for my pothole claim?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT Spend scorecard is RED — one of only a handful of councils nationally — because East Riding's
                projected capital spend falls short of its DfT allocation and its resurfacing programme is minimal relative
                to network size. For 2025/26 the council forecasts £20.413m capital spend against £22.015m allocated, and
                plans just 12km of carriageway resurfacing across 3,551km. That under-investment context matters when the
                council argues it lacked resources to prevent your defect — but Section 58 still turns on whether your
                specific pothole was reasonably inspected and repaired.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 1,911km — 54% of the East Riding network — and 22% were in RED condition at the last survey
                in 2024. That is roughly 420km of estate roads, village lanes and rural routes. The council surveys U-roads
                by Coarse Visual Inspection via RoadAI, not SCANNER, and admits SCANNER vehicles have historically been too
                large for many estate routes. Your strongest structural arguments often sit on this road class.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                East Riding says its A-roads are among the best in the country — does that weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Only if your incident was on an A-road. The council reports just 1% of A-roads in poor condition needing
                maintenance — and A-roads carry nearly half of traffic on only 10% of the network. If your pothole was on a
                B, C or unclassified road, the council's own data shows a different picture: approximately 4% of B/C roads
                in RED condition and U-roads deteriorating 2–4% annually under current investment levels.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs fell to 31,015 in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. East Riding still filled 31,015 potholes in 2024/25 — about 85 every day — and reactive pothole work
                consumed 22% of reactive maintenance spend that year, up from 16% in 2020/21. The five-year total is
                163,865 pothole repairs. The council's own goal is to reduce reactive costs by shifting to preventative
                work — but U-roads are still deteriorating annually.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why does East Riding not use SCANNER surveys on unclassified roads?
              </h3>
              <p className="text-surface-600 text-sm">
                The council states SCANNER is not used on the unclassified network because it is more expensive, provides
                analysis beyond what is necessary for U-roads, and the survey vehicle has historically been too large to
                traverse many estate routes. CVI via RoadAI is cheaper but less precise. A council choosing simpler surveys
                on 54% of its network cannot later claim the same detailed condition knowledge it has on SCANNER-surveyed A
                and B roads.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.eastriding.gov.uk/council/plans-and-policies/other-plans-and-policies-information/transport/highways-planned-maintenance/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              East Riding of Yorkshire Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
